/**
 * Admin Settings Page
 *
 * Manage tenants/organizations (super admin) and storage settings (storage.admin permission)
 */

import { useState, useCallback, useMemo } from 'react';
import {
  Typography,
  Button,
  Table,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  Popconfirm,
  message,
  Tag,
  Tooltip,
  Card,
  Row,
  Col,
  Statistic,
  Tabs,
  Descriptions,
  List,
  Avatar,
  Empty,
  Alert,
  Select,
  InputNumber,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  StopOutlined,
  ReloadOutlined,
  TeamOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  UserOutlined,
  CloudOutlined,
  ApiOutlined,
  SaveOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantsApi, Tenant, TenantUser } from '@api/tenants';
import { storageApi, type StorageConfigInput } from '@api/storage';
import { authApi } from '@api/auth';
import { useAuthStore } from '@store/authStore';
import { useAuth } from '@hooks/useAuth';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface TenantFormValues {
  name: string;
  slug: string;
}

export default function AdminSettingsPage() {
  const queryClient = useQueryClient();
  const { user, hasPermission } = useAuthStore();
  const { checkSession } = useAuth();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [form] = Form.useForm<TenantFormValues>();
  const [storageForm] = Form.useForm<StorageConfigInput>();
  const [testingStorage, setTestingStorage] = useState(false);
  const [selectedStorageTenantId, setSelectedStorageTenantId] = useState<string | undefined>(undefined);

  // Permission checks
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const canManageStorage = isSuperAdmin || hasPermission('storage.admin');

  // Determine default tab based on permissions
  const defaultTab = useMemo(() => {
    if (isSuperAdmin) return 'tenants';
    if (canManageStorage) return 'storage';
    return 'system';
  }, [isSuperAdmin, canManageStorage]);

  // Bootstrap admin handler
  const handleBootstrapAdmin = useCallback(async () => {
    if (!user?.email) return;
    setBootstrapping(true);
    try {
      const result = await authApi.bootstrapAdmin(user.email);
      message.success(result.message);
      // Refresh session to get updated permissions
      await checkSession();
      // Force page refresh to apply new admin status
      window.location.reload();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to bootstrap admin';
      message.error(errorMsg);
    } finally {
      setBootstrapping(false);
    }
  }, [user?.email, checkSession]);

  // Check if user has any admin access
  if (!isSuperAdmin && !canManageStorage) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Access Denied"
          description="This page requires administrator permissions."
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Card title="Claim Super Admin" size="small">
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            If this is a new installation or there are no super admins yet, you can claim super admin privileges.
          </Text>
          <Button
            type="primary"
            onClick={handleBootstrapAdmin}
            loading={bootstrapping}
          >
            Claim Super Admin Privileges
          </Button>
        </Card>
      </div>
    );
  }

  // Fetch tenants
  const { data: tenantsResponse, isLoading, refetch } = useQuery({
    queryKey: ['tenants', showInactive],
    queryFn: () => tenantsApi.list({
      is_active: showInactive ? undefined : true,
      sort_by: 'name',
      limit: 100,
    }),
  });

  const tenants = tenantsResponse?.items || [];
  const totalTenants = tenantsResponse?.total || 0;

  // Create tenant mutation
  const createMutation = useMutation({
    mutationFn: (data: TenantFormValues) => tenantsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'], exact: false });
      message.success('Tenant created successfully');
      handleCloseCreateModal();
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.error || 'Failed to create tenant';
      message.error(errorMsg);
    },
  });

  // Update tenant mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TenantFormValues> }) =>
      tenantsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'], exact: false });
      message.success('Tenant updated');
      handleCloseEditModal();
    },
    onError: () => message.error('Failed to update tenant'),
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      tenantsApi.update(id, { is_active }),
    onSuccess: (_, { is_active }) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'], exact: false });
      message.success(is_active ? 'Tenant activated' : 'Tenant deactivated');
    },
    onError: () => message.error('Failed to update tenant status'),
  });

  // Fetch storage config for selected tenant (or current tenant if not super admin)
  const { data: storageConfig, isLoading: storageLoading } = useQuery({
    queryKey: ['storage-config', selectedStorageTenantId],
    queryFn: () => storageApi.getConfig(selectedStorageTenantId),
  });

  // Save storage config mutation
  const saveStorageMutation = useMutation({
    mutationFn: (data: StorageConfigInput) => storageApi.saveConfig(data, selectedStorageTenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storage-config', selectedStorageTenantId] });
      message.success('Storage configuration saved');
    },
    onError: () => message.error('Failed to save storage configuration'),
  });

  // Test storage connection
  const handleTestStorage = useCallback(async () => {
    setTestingStorage(true);
    try {
      const result = await storageApi.testConnection(selectedStorageTenantId);
      if (result.success) {
        message.success('Connection successful!');
      } else {
        message.error(result.error || 'Connection failed');
      }
    } catch {
      message.error('Failed to test connection');
    } finally {
      setTestingStorage(false);
    }
  }, [selectedStorageTenantId]);

  // Save storage configuration
  const handleSaveStorage = useCallback(async () => {
    try {
      const values = await storageForm.validateFields();
      saveStorageMutation.mutate(values);
    } catch {
      // validation errors
    }
  }, [storageForm, saveStorageMutation]);

  // Handle tenant selection change for storage
  const handleStorageTenantChange = useCallback((tenantId: string | undefined) => {
    setSelectedStorageTenantId(tenantId);
    storageForm.resetFields();
  }, [storageForm]);

  // Handlers
  const handleOpenCreateModal = useCallback(() => {
    form.resetFields();
    setCreateModalOpen(true);
  }, [form]);

  const handleCloseCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    form.resetFields();
  }, [form]);

  const handleOpenEditModal = useCallback((tenant: Tenant) => {
    setSelectedTenant(tenant);
    form.setFieldsValue({
      name: tenant.name,
      slug: tenant.slug,
    });
    setEditModalOpen(true);
  }, [form]);

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setSelectedTenant(null);
    form.resetFields();
  }, [form]);

  const handleViewTenant = useCallback(async (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setViewModalOpen(true);
    setLoadingUsers(true);
    try {
      const response = await tenantsApi.getUsers(tenant.id);
      setTenantUsers(response.items);
    } catch {
      message.error('Failed to load tenant users');
      setTenantUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const handleCloseViewModal = useCallback(() => {
    setViewModalOpen(false);
    setSelectedTenant(null);
    setTenantUsers([]);
  }, []);

  const handleCreate = useCallback(async () => {
    try {
      const values = await form.validateFields();
      createMutation.mutate(values);
    } catch {
      // validation errors
    }
  }, [form, createMutation]);

  const handleUpdate = useCallback(async () => {
    if (!selectedTenant) return;
    try {
      const values = await form.validateFields();
      updateMutation.mutate({ id: selectedTenant.id, data: values });
    } catch {
      // validation errors
    }
  }, [form, selectedTenant, updateMutation]);

  const handleToggleActive = useCallback((tenant: Tenant) => {
    toggleActiveMutation.mutate({ id: tenant.id, is_active: !tenant.is_active });
  }, [toggleActiveMutation]);

  // Generate slug from name
  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    form.setFieldsValue({ slug });
  }, [form]);

  // Table columns
  const columns: ColumnsType<Tenant> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <Space>
          <Avatar
            size="small"
            style={{ backgroundColor: record.is_active ? '#52c41a' : '#d9d9d9' }}
          >
            {name.charAt(0).toUpperCase()}
          </Avatar>
          <span>{name}</span>
        </Space>
      ),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      render: (slug: string) => <Text code>{slug}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (is_active: boolean) =>
        is_active ? (
          <Tag icon={<CheckCircleOutlined />} color="success">Active</Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">Inactive</Tag>
        ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: unknown, record: Tenant) => (
        <Space size="small">
          <Tooltip title="View details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewTenant(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenEditModal(record)}
            />
          </Tooltip>
          <Tooltip title={record.is_active ? 'Deactivate' : 'Activate'}>
            <Popconfirm
              title={record.is_active ? 'Deactivate tenant?' : 'Activate tenant?'}
              description={record.is_active
                ? 'Users will lose access to this organization.'
                : 'Users will regain access to this organization.'
              }
              onConfirm={() => handleToggleActive(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                size="small"
                danger={record.is_active}
                icon={record.is_active ? <StopOutlined /> : <CheckCircleOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Calculate stats
  const activeTenants = tenants.filter(t => t.is_active).length;
  const inactiveTenants = tenants.filter(t => !t.is_active).length;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>System Administration</Title>
        <Text type="secondary">Manage organizations and system settings</Text>
      </div>

      <Tabs
        defaultActiveKey={defaultTab}
        items={[
          // Organizations tab - super admin only
          ...(isSuperAdmin ? [{
            key: 'tenants',
            label: (
              <span>
                <TeamOutlined />
                Organizations
              </span>
            ),
            children: (
              <>
                {/* Stats */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={8}>
                    <Card>
                      <Statistic
                        title="Total Organizations"
                        value={totalTenants}
                        prefix={<TeamOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card>
                      <Statistic
                        title="Active"
                        value={activeTenants}
                        valueStyle={{ color: '#52c41a' }}
                        prefix={<CheckCircleOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card>
                      <Statistic
                        title="Inactive"
                        value={inactiveTenants}
                        valueStyle={{ color: '#d9d9d9' }}
                        prefix={<CloseCircleOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>

                {/* Actions */}
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleOpenCreateModal}
                    >
                      Create Organization
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                      Refresh
                    </Button>
                  </Space>
                  <Space>
                    <Text type="secondary">Show inactive:</Text>
                    <Switch
                      checked={showInactive}
                      onChange={setShowInactive}
                      size="small"
                    />
                  </Space>
                </div>

                {/* Table */}
                <Table
                  columns={columns}
                  dataSource={tenants}
                  rowKey="id"
                  loading={isLoading}
                  pagination={{
                    total: totalTenants,
                    pageSize: 20,
                    showSizeChanger: false,
                    showTotal: (total) => `${total} organizations`,
                  }}
                />
              </>
            ),
          }] : []),
          // Storage tab - super admin or storage.admin permission
          ...(canManageStorage ? [{
            key: 'storage',
            label: (
              <span>
                <CloudOutlined />
                Storage
              </span>
            ),
            children: (
              <Row gutter={24}>
                <Col span={16}>
                  {/* Tenant selector for super admins */}
                  {isSuperAdmin && (
                    <Card style={{ marginBottom: 16 }}>
                      <Form.Item label="Configure storage for tenant" style={{ marginBottom: 0 }}>
                        <Select
                          placeholder="Select a tenant to configure"
                          value={selectedStorageTenantId}
                          onChange={handleStorageTenantChange}
                          allowClear
                          showSearch
                          optionFilterProp="children"
                          style={{ width: 300 }}
                        >
                          {tenants.map((t) => (
                            <Select.Option key={t.id} value={t.id}>
                              {t.name} {!t.is_active && <Tag color="red">Inactive</Tag>}
                            </Select.Option>
                          ))}
                        </Select>
                        {!selectedStorageTenantId && (
                          <Text type="secondary" style={{ marginLeft: 12 }}>
                            Select a tenant to view/edit their storage settings
                          </Text>
                        )}
                      </Form.Item>
                    </Card>
                  )}
                  <Card title={
                    <Space>
                      <span>R2/S3 Storage Configuration</span>
                      {selectedStorageTenantId && (
                        <Tag color="blue">
                          {tenants.find(t => t.id === selectedStorageTenantId)?.name}
                        </Tag>
                      )}
                    </Space>
                  } extra={
                    <Space>
                      <Button
                        icon={testingStorage ? <LoadingOutlined /> : <ApiOutlined />}
                        onClick={handleTestStorage}
                        disabled={!storageConfig?.has_credentials || testingStorage || (isSuperAdmin && !selectedStorageTenantId)}
                      >
                        Test Connection
                      </Button>
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={handleSaveStorage}
                        loading={saveStorageMutation.isPending}
                        disabled={isSuperAdmin && !selectedStorageTenantId}
                      >
                        Save
                      </Button>
                    </Space>
                  }>
                    {isSuperAdmin && !selectedStorageTenantId ? (
                      <Empty description="Select a tenant above to configure their storage" />
                    ) : storageLoading ? (
                      <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                    ) : (
                      <Form
                        form={storageForm}
                        layout="vertical"
                        initialValues={{
                          provider: storageConfig?.provider || 'r2',
                          bucket_name: storageConfig?.bucket_name || '',
                          endpoint_url: storageConfig?.endpoint_url || '',
                          region: storageConfig?.region || 'auto',
                          public_url_prefix: storageConfig?.public_url_prefix || '',
                          max_file_size_mb: storageConfig?.max_file_size_mb || 50,
                          allowed_file_types: storageConfig?.allowed_file_types || '*',
                        }}
                      >
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item name="provider" label="Storage Provider" rules={[{ required: true }]}>
                              <Select>
                                <Select.Option value="r2">Cloudflare R2</Select.Option>
                                <Select.Option value="s3">Amazon S3</Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item name="bucket_name" label="Bucket Name" rules={[{ required: true }]}>
                              <Input placeholder="my-bucket" />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              name="access_key_id"
                              label="Access Key ID"
                              rules={[{ required: !storageConfig?.has_credentials }]}
                              extra={storageConfig?.has_credentials ? 'Leave blank to keep existing' : undefined}
                            >
                              <Input.Password placeholder={storageConfig?.has_credentials ? '••••••••' : 'Enter access key'} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name="secret_access_key"
                              label="Secret Access Key"
                              rules={[{ required: !storageConfig?.has_credentials }]}
                              extra={storageConfig?.has_credentials ? 'Leave blank to keep existing' : undefined}
                            >
                              <Input.Password placeholder={storageConfig?.has_credentials ? '••••••••' : 'Enter secret key'} />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item name="endpoint_url" label="Endpoint URL" extra="For R2: https://<account_id>.r2.cloudflarestorage.com">
                              <Input placeholder="https://..." />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item name="region" label="Region">
                              <Input placeholder="auto" />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item name="public_url_prefix" label="Public URL Prefix" extra="Custom domain for serving files">
                              <Input placeholder="https://files.example.com" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item name="max_file_size_mb" label="Max File Size (MB)">
                              <InputNumber min={1} max={500} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Form>
                    )}
                  </Card>
                </Col>
                <Col span={8}>
                  <Card title="Storage Usage">
                    {storageConfig ? (
                      <>
                        <Statistic
                          title="Files Stored"
                          value={storageConfig.file_count}
                          style={{ marginBottom: 16 }}
                        />
                        <Statistic
                          title="Storage Used"
                          value={((storageConfig.storage_used_bytes || 0) / 1024 / 1024).toFixed(2)}
                          suffix="MB"
                          style={{ marginBottom: 16 }}
                        />
                        <div style={{ marginTop: 16 }}>
                          <Text type="secondary">Status:</Text>
                          {storageConfig.is_active ? (
                            <Tag color="success" style={{ marginLeft: 8 }}>Configured</Tag>
                          ) : (
                            <Tag color="warning" style={{ marginLeft: 8 }}>Not Configured</Tag>
                          )}
                        </div>
                      </>
                    ) : (
                      <Empty description="No storage configured" />
                    )}
                  </Card>
                </Col>
              </Row>
            ),
          }] : []),
          // System info - visible to anyone who can access this page
          {
            key: 'system',
            label: (
              <span>
                <AppstoreOutlined />
                System Info
              </span>
            ),
            children: (
              <Card>
                <Descriptions title="System Information" column={2}>
                  <Descriptions.Item label="Version">1.0.0</Descriptions.Item>
                  <Descriptions.Item label="Environment">
                    {import.meta.env.MODE}
                  </Descriptions.Item>
                  <Descriptions.Item label="API URL">
                    {import.meta.env.VITE_API_URL || '/api'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Build Date">
                    {new Date().toLocaleDateString()}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            ),
          },
        ]}
      />

      {/* Create Modal */}
      <Modal
        title="Create Organization"
        open={createModalOpen}
        onOk={handleCreate}
        onCancel={handleCloseCreateModal}
        confirmLoading={createMutation.isPending}
        okText="Create"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Organization Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="Acme Inc." onChange={handleNameChange} />
          </Form.Item>
          <Form.Item
            name="slug"
            label="URL Slug"
            rules={[
              { required: true, message: 'Please enter a slug' },
              { pattern: /^[a-z0-9-]+$/, message: 'Lowercase letters, numbers, and hyphens only' },
            ]}
            extra="Used in URLs. Auto-generated from name."
          >
            <Input placeholder="acme-inc" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit Organization"
        open={editModalOpen}
        onOk={handleUpdate}
        onCancel={handleCloseEditModal}
        confirmLoading={updateMutation.isPending}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Organization Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="Acme Inc." />
          </Form.Item>
          <Form.Item
            name="slug"
            label="URL Slug"
            rules={[
              { required: true, message: 'Please enter a slug' },
              { pattern: /^[a-z0-9-]+$/, message: 'Lowercase letters, numbers, and hyphens only' },
            ]}
          >
            <Input placeholder="acme-inc" />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Modal */}
      <Modal
        title={selectedTenant?.name || 'Organization Details'}
        open={viewModalOpen}
        onCancel={handleCloseViewModal}
        footer={[
          <Button key="close" onClick={handleCloseViewModal}>Close</Button>,
          <Button
            key="edit"
            type="primary"
            onClick={() => {
              handleCloseViewModal();
              if (selectedTenant) handleOpenEditModal(selectedTenant);
            }}
          >
            Edit
          </Button>,
        ]}
        width={600}
      >
        {selectedTenant && (
          <>
            <Descriptions column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Name">{selectedTenant.name}</Descriptions.Item>
              <Descriptions.Item label="Slug">
                <Text code>{selectedTenant.slug}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {selectedTenant.is_active ? (
                  <Tag color="success">Active</Tag>
                ) : (
                  <Tag color="default">Inactive</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {dayjs(selectedTenant.created_at).format('MMM D, YYYY')}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5}>Users ({tenantUsers.length})</Title>
            {loadingUsers ? (
              <div style={{ textAlign: 'center', padding: 24 }}>Loading users...</div>
            ) : tenantUsers.length === 0 ? (
              <Empty description="No users in this organization" />
            ) : (
              <List
                size="small"
                dataSource={tenantUsers}
                renderItem={(user) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar icon={<UserOutlined />} size="small" />}
                      title={user.full_name}
                      description={
                        <Space>
                          <Text type="secondary">{user.email}</Text>
                          <Tag>{user.role.name}</Tag>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </>
        )}
      </Modal>
    </div>
  );
}
